import { useCallback, useState } from 'react';
import { getCalDatesByScheduleId } from '../../../api/schedule';
import { CalDateInfo, SlotInfo, SlotType } from '../../../types/schedule.types';
import { mergeCalDateShift, mergeSortIds, normalizeToSortedIds } from '../../../utils/calDateSlotsSortUtils';

interface UseCalDateActionsParams {
  scheduleID: string | undefined;
  currentDate: Date | null;
  setCurrentDate: (d: Date) => void;
  calDates: CalDateInfo[] | undefined;
  slotList: SlotInfo[];
  formatDateToString: (d: Date) => string;
  calDateDtToLocalStr: (dt: string | undefined) => string;
  createSlotData: (scheduleID: string, data: Partial<SlotInfo>) => Promise<string | undefined>;
  upsertCalDateData: (scheduleID: string, data: Partial<CalDateInfo>) => Promise<void>;
  shiftSlotsDateData: (scheduleID: string, slotIDs: string[], days: number) => Promise<void>;
  shiftCalDatesDateData: (scheduleID: string, calDateIDs: string[], days: number) => Promise<void>;
  deleteCalDateData: (scheduleID: string, calDateID: string) => Promise<void>;
  refreshScheduleCalendarData: () => Promise<void>;
  setServerSyncedField: (key: string, value: string) => void;
  t: (key: string) => string;
}

export function useCalDateActions({
  scheduleID,
  currentDate,
  setCurrentDate,
  calDates,
  slotList,
  formatDateToString,
  calDateDtToLocalStr,
  createSlotData,
  upsertCalDateData,
  shiftSlotsDateData,
  shiftCalDatesDateData,
  deleteCalDateData,
  refreshScheduleCalendarData,
  setServerSyncedField,
  t,
}: UseCalDateActionsParams) {
  const [deleteDateDialogVisible, setDeleteDateDialogVisible] = useState(false);

  const handleAddNote = useCallback(async () => {
    if (!scheduleID || !currentDate) return;
    const currentDateStr = formatDateToString(currentDate);
    try {
      const newSlotID = await createSlotData(scheduleID, {
        slotType: SlotType.NOTE,
        slotName: '',
        slotBelongScheduleID: scheduleID,
        slotDate: currentDateStr,
        slotEndDay: currentDateStr,
      });
      if (newSlotID) {
        const todayCalDate = calDates?.find((pd) => {
          if (!pd.calDateDt) return false;
          return pd.calDateDt.split('T')[0] === currentDateStr;
        });
        const baseIds = normalizeToSortedIds(todayCalDate?.calDateSlotsSort);
        const newSortIds = [...baseIds, newSlotID];
        await upsertCalDateData(scheduleID, {
          calDateDt: currentDateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: newSortIds,
          calDateShift: todayCalDate?.calDateShift,
          calDateTittle: todayCalDate?.calDateTittle,
        });
      }
      await refreshScheduleCalendarData();
    } catch (error) {
      console.error('添加便签失败:', error);
      alert(t('calendar.addNoteFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [
    scheduleID,
    currentDate,
    formatDateToString,
    createSlotData,
    calDates,
    upsertCalDateData,
    refreshScheduleCalendarData,
    t,
  ]);

  const handleInsertDate = useCallback(async () => {
    if (!currentDate || !scheduleID) return;
    try {
      const currentDateStr = formatDateToString(currentDate);
      const calDatesRes = await getCalDatesByScheduleId(scheduleID);
      const calDatesToUse = calDatesRes?.data ?? calDates ?? [];

      const relatedSlots = slotList.filter((pt) => {
        if (pt.slotBelongScheduleID !== scheduleID || !pt.slotDate) return false;
        const slotDateStr = pt.slotDate.split('T')[0];
        return slotDateStr >= currentDateStr;
      });
      const relatedCalDates = calDatesToUse.filter((pd) => {
        if (pd.calDateBelongScheduleID !== scheduleID || !pd.calDateDt) return false;
        const dateStr = calDateDtToLocalStr(pd.calDateDt);
        return dateStr >= currentDateStr;
      });

      if (relatedSlots.length > 0) {
        await shiftSlotsDateData(scheduleID, relatedSlots.map((pt) => pt.slotID), 1);
      }
      if (relatedCalDates.length > 0) {
        await shiftCalDatesDateData(scheduleID, relatedCalDates.map((pd) => pd.calDateID), 1);
      }
      await refreshScheduleCalendarData();
    } catch (error) {
      console.error('插入日期失败:', error);
      alert(t('schedule.insertDateFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [
    currentDate,
    scheduleID,
    formatDateToString,
    calDates,
    slotList,
    calDateDtToLocalStr,
    shiftSlotsDateData,
    shiftCalDatesDateData,
    refreshScheduleCalendarData,
    t,
  ]);

  const handleDeleteDateClick = useCallback(() => {
    if (!currentDate || !scheduleID) return;
    setDeleteDateDialogVisible(true);
  }, [currentDate, scheduleID]);

  const handleDeleteDateConfirm = useCallback(async () => {
    if (!currentDate || !scheduleID) return;
    try {
      const currentDateStr = formatDateToString(currentDate);
      const previousDate = new Date(currentDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDateStr = formatDateToString(previousDate);

      const calDatesRes = await getCalDatesByScheduleId(scheduleID);
      const calDatesToUse = calDatesRes?.data ?? calDates ?? [];

      const todaySlots = slotList.filter((pt) => {
        if (pt.slotBelongScheduleID !== scheduleID || !pt.slotDate) return false;
        return pt.slotDate.split('T')[0] === currentDateStr;
      });
      const relatedSlots = slotList.filter((pt) => {
        if (pt.slotBelongScheduleID !== scheduleID || !pt.slotDate) return false;
        return pt.slotDate.split('T')[0] >= currentDateStr;
      });
      const relatedCalDates = calDatesToUse.filter((pd) => {
        if (pd.calDateBelongScheduleID !== scheduleID || !pd.calDateDt) return false;
        return calDateDtToLocalStr(pd.calDateDt) > currentDateStr;
      });
      const todayCalDate = calDatesToUse.find((pd) => {
        if (pd.calDateBelongScheduleID !== scheduleID || !pd.calDateDt) return false;
        return calDateDtToLocalStr(pd.calDateDt) === currentDateStr;
      });
      const previousCalDate = calDatesToUse.find((pd) => {
        if (pd.calDateBelongScheduleID !== scheduleID || !pd.calDateDt) return false;
        return calDateDtToLocalStr(pd.calDateDt) === previousDateStr;
      });

      const todaySlotIDSet = new Set(todaySlots.map((pt) => pt.slotID));
      const todayIdsRaw = normalizeToSortedIds(todayCalDate?.calDateSlotsSort).filter((id) =>
        todaySlotIDSet.has(id),
      );
      const todayInSort = new Set(normalizeToSortedIds(todayCalDate?.calDateSlotsSort));
      const todayNotInSort = todaySlots.filter((pt) => !todayInSort.has(pt.slotID)).map((pt) => pt.slotID);
      const todayFlat = [...todayIdsRaw, ...todayNotInSort];

      const previousSlots = slotList.filter((pt) => {
        if (pt.slotBelongScheduleID !== scheduleID || !pt.slotDate) return false;
        return pt.slotDate.split('T')[0] === previousDateStr;
      });
      const prevSoltSet = new Set(previousSlots.map((pt) => pt.slotID));
      const previousFlat = normalizeToSortedIds(previousCalDate?.calDateSlotsSort).filter((id) =>
        prevSoltSet.has(id),
      );
      const finalMergedSort = mergeSortIds(previousFlat, todayFlat);
      const finalCalDateShift = mergeCalDateShift(
        previousCalDate?.calDateShift,
        todayCalDate?.calDateShift,
      );

      if (todayFlat.length > 0) {
        await upsertCalDateData(scheduleID, {
          calDateDt: previousDateStr,
          calDateBelongScheduleID: scheduleID,
          calDateSlotsSort: finalMergedSort,
          ...(finalCalDateShift !== undefined ? { calDateShift: finalCalDateShift } : {}),
          calDateTittle: previousCalDate?.calDateTittle,
        });
      }
      if (relatedSlots.length > 0) {
        await shiftSlotsDateData(scheduleID, relatedSlots.map((pt) => pt.slotID), -1);
      }
      if (todayCalDate?.calDateID) {
        await deleteCalDateData(scheduleID, todayCalDate.calDateID);
      }
      if (relatedCalDates.length > 0) {
        await shiftCalDatesDateData(scheduleID, relatedCalDates.map((pd) => pd.calDateID), -1);
      }
      await refreshScheduleCalendarData();
      setDeleteDateDialogVisible(false);
      setCurrentDate(previousDate);
      setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${previousDateStr}`);
    } catch (error) {
      console.error('删除日期失败:', error);
      alert(t('schedule.deleteDateFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [
    currentDate,
    scheduleID,
    formatDateToString,
    calDates,
    slotList,
    calDateDtToLocalStr,
    upsertCalDateData,
    shiftSlotsDateData,
    deleteCalDateData,
    shiftCalDatesDateData,
    refreshScheduleCalendarData,
    setCurrentDate,
    setServerSyncedField,
    t,
  ]);

  return {
    deleteDateDialogVisible,
    setDeleteDateDialogVisible,
    handleAddNote,
    handleInsertDate,
    handleDeleteDateClick,
    handleDeleteDateConfirm,
  };
}
